<template>
    <div class="flex flex-col dark:text-white">
        <div class="flex flex-col my-5">
            <form
                class="grid grid-cols-1 md:grid-cols-12 gap-3 items-end"
                @submit.prevent="resetAndFetch()"
            >
                <div class="md:col-span-5">
                    <label class="block text-xs text-slate-400 mb-1">Email</label>
                    <input
                        v-model="filters.email"
                        type="text"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="email@exemplo.com"
                    />
                </div>
                <div class="md:col-span-3">
                    <label class="block text-xs text-slate-400 mb-1">CPF</label>
                    <input
                        v-model="filters.cpf"
                        type="text"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="000.000.000-00"
                    />
                </div>
                <div class="md:col-span-2">
                    <label class="block text-xs text-slate-400 mb-1">Tipo</label>
                    <select
                        v-model="filters.type"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                        <option value="">Todos</option>
                        <option value="person">PF</option>
                        <option value="company">PJ</option>
                    </select>
                </div>
                <div class="md:col-span-2 flex gap-2">
                    <button
                        type="button"
                        @click.prevent="clearFilters"
                        class="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-indigo-500 bg-transparent border-2 border-indigo-500 rounded-md hover:text-white hover:bg-indigo-500"
                    >
                        Limpar
                    </button>
                    <button
                        type="submit"
                        :class="{ 'cursor-not-allowed': loading }"
                        class="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-white bg-indigo-500 rounded-md shadow hover:bg-indigo-400"
                    >
                        <svg
                            v-if="loading"
                            class="w-5 h-5 mr-2 -ml-1 text-white animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Filtrar
                    </button>
                </div>
            </form>
        </div>

        <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full sm:px-6 lg:px-8">
                <div class="relative overflow-hidden not-prose bg-slate-50 rounded-xl dark:bg-slate-800/25">
                    <table class="w-full text-sm border-collapse table-auto">
                        <thead>
                            <tr>
                                <th class="px-6 py-4 text-sm font-medium text-left">Usuário</th>
                                <th class="px-6 py-4 text-sm font-medium text-left">CPF</th>
                                <th class="px-6 py-4 text-sm font-medium text-left">Tipo</th>
                                <th class="px-6 py-4 text-sm font-medium text-left">Estado do fluxo</th>
                                <th class="px-6 py-4 text-sm font-medium text-left">Atualizado em</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-slate-800">
                            <tr v-for="user in users" :key="user.id">
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    <button class="text-indigo-500 hover:underline" @click.prevent="openModal(user)">
                                        {{ user.name }}<br />
                                        <span class="text-xs text-slate-400">{{ user.email }}</span>
                                    </button>
                                </td>
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    {{ user.effective_cpf || '—' }}
                                </td>
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    {{ user.type === 'company' ? 'PJ' : 'PF' }}
                                </td>
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    <span :class="flowBadge(user.flow_state)" class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium">
                                        {{ flowLabel(user.flow_state) }}
                                    </span>
                                </td>
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    {{ formatDate(user.updated_at) }}
                                </td>
                            </tr>
                            <tr v-if="!loading && users.length === 0">
                                <td colspan="5" class="p-6 text-center text-slate-400">
                                    Nenhum usuário encontrado.
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="flex w-full my-5">
                        <div class="flex flex-col items-center mx-auto">
                            <span class="mb-1">
                                Exibindo {{ pagination.from }} a {{ pagination.to }} de {{ pagination.total }}
                            </span>
                            <pagination
                                :pagination="pagination"
                                :offset="1"
                                @paginate="fetch"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <biometric-revalidation-modal
            v-if="modalOpen"
            :user-id="selectedUserId"
            @close="closeModal"
            @forced="onForced"
        />
    </div>
</template>

<script>
import Pagination from "../../components/layouts/Pagination.vue";
import BiometricRevalidationModal from "./BiometricRevalidationModal.vue";

export default {
    components: { Pagination, BiometricRevalidationModal },
    data() {
        return {
            loading: false,
            users: [],
            pagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 1,
                from: 0,
                to: 0,
            },
            filters: {
                email: '',
                cpf: '',
                type: '',
            },
            modalOpen: false,
            selectedUserId: null,
        };
    },
    mounted() {
        this.fetch();
    },
    methods: {
        resetAndFetch() {
            this.users = [];
            this.pagination.current_page = 1;
            this.fetch();
        },
        clearFilters() {
            this.filters.email = '';
            this.filters.cpf = '';
            this.filters.type = '';
            this.resetAndFetch();
        },
        fetch() {
            this.loading = true;
            const params = {
                page: this.pagination.current_page,
                per_page: this.pagination.per_page,
                'filter[email]': this.filters.email,
                'filter[cpf]': this.filters.cpf,
                'filter[type]': this.filters.type,
            };
            axios.get('/api/kyc/biometric-revalidation', { params })
                .then(({ data }) => {
                    this.users = data.data;
                    this.pagination = { ...this.pagination, ...data };
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        openModal(user) {
            this.selectedUserId = user.id;
            this.modalOpen = true;
        },
        closeModal() {
            this.modalOpen = false;
            this.selectedUserId = null;
        },
        onForced() {
            this.closeModal();
            this.fetch();
        },
        flowLabel(state) {
            return {
                validated: 'Validado',
                pending: 'Aguardando biometria',
                blocked: 'Bloqueado',
                not_started: 'Não iniciou',
            }[state] ?? state;
        },
        flowBadge(state) {
            return {
                validated: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                blocked: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                not_started: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
            }[state] ?? 'bg-gray-100 text-gray-800';
        },
        formatDate(value) {
            if (!value) return '-';
            try {
                return new Date(value).toLocaleString('pt-BR');
            } catch {
                return value;
            }
        },
    },
};
</script>
